export const HOMEBREW_SCHEMA_VERSION = 1;

const commonMetaFields = [
  { key: 'name', label: 'Name', type: 'string', required: true },
  { key: 'description', label: 'Description', type: 'string', required: false },
  { key: 'source', label: 'Source', type: 'string', required: false },
  { key: 'tags', label: 'Tags', type: 'string[]', required: false },
];

export const HOMEBREW_SCHEMAS_2014 = {
  race: {
    label: 'Race',
    requiredDataFields: ['size', 'speed', 'languages', 'traits'],
    sections: [
      { id: 'basics', title: 'Basics', fields: commonMetaFields },
      {
        id: 'mechanics',
        title: 'Mechanics',
        fields: [
          { key: 'size', label: 'Size', type: 'string', required: true },
          { key: 'speed', label: 'Walking Speed', type: 'number', required: true },
          { key: 'speeds', label: 'Movement Modes', type: 'object', required: false },
          { key: 'ability_score_increase', label: 'Ability Score Increase', type: 'object', required: false },
          { key: 'senses', label: 'Senses', type: 'object', required: false },
        ],
      },
      {
        id: 'grants',
        title: 'Grants',
        fields: [
          { key: 'languages', label: 'Languages', type: 'string[]', required: true },
          { key: 'proficiencies', label: 'Proficiencies', type: 'object', required: false },
          { key: 'traits', label: 'Traits', type: 'object[]', required: true },
        ],
      },
    ],
  },
  class: {
    label: 'Class',
    requiredDataFields: ['hit_die', 'saving_throws', 'features_by_level'],
    sections: [
      { id: 'basics', title: 'Basics', fields: commonMetaFields },
      {
        id: 'mechanics',
        title: 'Core Mechanics',
        fields: [
          { key: 'hit_die', label: 'Hit Die', type: 'number', required: true },
          { key: 'saving_throws', label: 'Saving Throws', type: 'string[]', required: true },
          { key: 'primary_ability', label: 'Primary Ability', type: 'string[]', required: false },
          { key: 'proficiencies', label: 'Proficiencies', type: 'object', required: false },
          { key: 'skill_choices', label: 'Skill Choices', type: 'object', required: false },
        ],
      },
      {
        id: 'progression',
        title: 'Progression',
        fields: [
          { key: 'features_by_level', label: 'Features By Level', type: 'object[]', required: true },
          { key: 'spellcasting', label: 'Spellcasting', type: 'object', required: false },
          { key: 'starting_equipment', label: 'Starting Equipment', type: 'object[]', required: false },
        ],
      },
    ],
  },
  subclass: {
    label: 'Subclass',
    requiredDataFields: ['parent_class', 'features_by_level'],
    sections: [
      { id: 'basics', title: 'Basics', fields: commonMetaFields },
      {
        id: 'mechanics',
        title: 'Core Mechanics',
        fields: [
          { key: 'parent_class', label: 'Parent Class', type: 'string', required: true },
          { key: 'features_by_level', label: 'Features By Level', type: 'object[]', required: true },
          { key: 'bonus_spells', label: 'Bonus Spells', type: 'object[]', required: false },
        ],
      },
    ],
  },
  background: {
    label: 'Background',
    requiredDataFields: ['skill_proficiencies'],
    sections: [
      { id: 'basics', title: 'Basics', fields: commonMetaFields },
      {
        id: 'grants',
        title: 'Grants',
        fields: [
          { key: 'skill_proficiencies', label: 'Skill Proficiencies', type: 'string[]', required: true },
          { key: 'tool_proficiencies', label: 'Tool Proficiencies', type: 'string[]', required: false },
          { key: 'languages', label: 'Languages', type: 'object|string[]', required: false },
          { key: 'equipment', label: 'Starting Equipment', type: 'object[]', required: false },
          { key: 'feature', label: 'Background Feature', type: 'object', required: false },
        ],
      },
    ],
  },
  feat: {
    label: 'Feat',
    requiredDataFields: ['benefits'],
    sections: [
      { id: 'basics', title: 'Basics', fields: commonMetaFields },
      {
        id: 'mechanics',
        title: 'Feat Mechanics',
        fields: [
          { key: 'prerequisites', label: 'Prerequisites', type: 'object[]', required: false },
          { key: 'ability_score_increase', label: 'Ability Score Increase', type: 'object', required: false },
          { key: 'benefits', label: 'Benefits', type: 'object[]', required: true },
        ],
      },
    ],
  },
  spell: {
    label: 'Spell',
    requiredDataFields: ['level', 'school', 'casting_time', 'range', 'duration', 'description'],
    sections: [
      { id: 'basics', title: 'Basics', fields: commonMetaFields },
      {
        id: 'mechanics',
        title: 'Spell Mechanics',
        fields: [
          { key: 'level', label: 'Level', type: 'number', required: true },
          { key: 'school', label: 'School', type: 'string', required: true },
          { key: 'casting_time', label: 'Casting Time', type: 'string', required: true },
          { key: 'range', label: 'Range', type: 'string', required: true },
          { key: 'duration', label: 'Duration', type: 'string', required: true },
          { key: 'components', label: 'Components', type: 'object', required: false },
          { key: 'classes', label: 'Classes', type: 'string[]', required: false },
          { key: 'ritual', label: 'Ritual', type: 'boolean', required: false },
          { key: 'concentration', label: 'Concentration', type: 'boolean', required: false },
          { key: 'save', label: 'Saving Throw Ability', type: 'string', required: false },
          { key: 'attack_type', label: 'Attack Type', type: 'string', required: false },
          { key: 'damage', label: 'Damage', type: 'object', required: false },
          { key: 'healing', label: 'Healing', type: 'object', required: false },
          { key: 'higher_levels', label: 'At Higher Levels', type: 'string', required: false },
          { key: 'description', label: 'Description', type: 'string', required: true },
        ],
      },
    ],
  },
  monster: {
    label: 'Monster',
    requiredDataFields: ['size', 'type', 'challenge_rating', 'armor_class', 'hit_points', 'ability_scores', 'actions'],
    sections: [
      { id: 'basics', title: 'Basics', fields: commonMetaFields },
      {
        id: 'mechanics',
        title: 'Stat Block',
        fields: [
          { key: 'size', label: 'Size', type: 'string', required: true },
          { key: 'type', label: 'Type', type: 'string', required: true },
          { key: 'alignment', label: 'Alignment', type: 'string', required: false },
          { key: 'challenge_rating', label: 'Challenge Rating', type: 'string|number', required: true },
          { key: 'armor_class', label: 'Armor Class', type: 'number', required: true },
          { key: 'hit_points', label: 'Hit Points', type: 'object', required: true },
          { key: 'speed', label: 'Speed', type: 'object', required: false },
          { key: 'ability_scores', label: 'Ability Scores', type: 'object', required: true },
          { key: 'skills', label: 'Skills', type: 'object', required: false },
          { key: 'saving_throws', label: 'Saving Throws', type: 'object', required: false },
          { key: 'senses', label: 'Senses', type: 'object', required: false },
          { key: 'languages', label: 'Languages', type: 'string[]', required: false },
          { key: 'traits', label: 'Traits', type: 'object[]', required: false },
          { key: 'actions', label: 'Actions', type: 'object[]', required: true },
          { key: 'reactions', label: 'Reactions', type: 'object[]', required: false },
          { key: 'legendary_actions', label: 'Legendary Actions', type: 'object[]', required: false },
        ],
      },
    ],
  },
  item: {
    label: 'Item',
    requiredDataFields: ['category', 'description'],
    sections: [
      { id: 'basics', title: 'Basics', fields: commonMetaFields },
      {
        id: 'mechanics',
        title: 'Item Mechanics',
        fields: [
          { key: 'category', label: 'Category', type: 'string', required: true },
          { key: 'rarity', label: 'Rarity', type: 'string', required: false },
          { key: 'weight', label: 'Weight', type: 'number', required: false },
          { key: 'cost', label: 'Cost', type: 'object', required: false },
          { key: 'requires_attunement', label: 'Requires Attunement', type: 'boolean', required: false },
          { key: 'effects', label: 'Effects', type: 'object[]', required: false },
          { key: 'description', label: 'Description', type: 'string', required: true },
        ],
      },
    ],
  },
  weapon: {
    label: 'Weapon',
    requiredDataFields: ['category', 'damage'],
    sections: [
      { id: 'basics', title: 'Basics', fields: commonMetaFields },
      {
        id: 'mechanics',
        title: 'Weapon Mechanics',
        fields: [
          { key: 'category', label: 'Category', type: 'string', required: true },
          { key: 'weapon_type', label: 'Weapon Type', type: 'string', required: false },
          { key: 'damage', label: 'Damage', type: 'object', required: true },
          { key: 'versatile_damage', label: 'Versatile Damage', type: 'object', required: false },
          { key: 'properties', label: 'Properties', type: 'string[]', required: false },
          { key: 'range', label: 'Range', type: 'object', required: false },
          { key: 'description', label: 'Description', type: 'string', required: false },
        ],
      },
    ],
  },
  armor: {
    label: 'Armor',
    requiredDataFields: ['category', 'ac'],
    sections: [
      { id: 'basics', title: 'Basics', fields: commonMetaFields },
      {
        id: 'mechanics',
        title: 'Armor Mechanics',
        fields: [
          { key: 'category', label: 'Category', type: 'string', required: true },
          { key: 'armor_type', label: 'Armor Type', type: 'string', required: false },
          { key: 'ac', label: 'Armor Class Model', type: 'object', required: true },
          { key: 'strength_requirement', label: 'Strength Requirement', type: 'number', required: false },
          { key: 'stealth_disadvantage', label: 'Stealth Disadvantage', type: 'boolean', required: false },
          { key: 'description', label: 'Description', type: 'string', required: false },
        ],
      },
    ],
  },
  language: {
    label: 'Language',
    requiredDataFields: ['type'],
    sections: [
      { id: 'basics', title: 'Basics', fields: commonMetaFields },
      {
        id: 'metadata',
        title: 'Language Metadata',
        fields: [
          { key: 'type', label: 'Type', type: 'string', required: true },
          { key: 'script', label: 'Script', type: 'string', required: false },
          { key: 'typical_speakers', label: 'Typical Speakers', type: 'string[]', required: false },
          { key: 'entries', label: 'Entries', type: 'string[]', required: false },
        ],
      },
    ],
  },
};

export function getHomebrewSchema(type) {
  return HOMEBREW_SCHEMAS_2014[type] || null;
}
